/**
 * Utility functions for recording custom metrics to Datadog RUM
 */

import { isDatadogInitialized } from './datadogrum';

export const CHECKOUT_CLICK_TO_WIDGET_RENDER_METRIC_NAME = 'checkout.click_to_widget_render';

export interface CheckoutClickToWidgetRenderContext {
  durationMs: number;
  eventId?: string;
  stage?: string;
}

// `@datadog/browser-rum-slim` is loaded asynchronously so this module stays safe when imported from SSR/tests and chunk load stays aligned with DatadogInit (same pattern as above).
async function loadDatadogRum() {
  const { datadogRum } = await import('@datadog/browser-rum-slim');
  return datadogRum;
}

/**
 * Records a custom timing metric to Datadog RUM
 * @param metricName - Name of the metric to record
 * @param durationMs - Duration in milliseconds
 * @param actionContext - Extra fields merged into `addAction` (`duration` / `timestamp` always set last)
 */
export const recordDatadogRumTiming = async (
  metricName: string,
  durationMs: number,
  actionContext?: Record<string, unknown>,
) => {
  try {
    if (isDatadogInitialized) {
      const datadogRum = await loadDatadogRum();

      datadogRum.addTiming(metricName, durationMs);

      datadogRum.addAction(metricName, {
        ...(actionContext ?? {}),
        duration: durationMs,
        timestamp: Date.now(),
      });
    }
  } catch (error) {
    console.error('Error recording Datadog RUM timing:', error);
  }
};

/**
 * Records a component render timing to Datadog RUM
 * Also creates a performance mark/measure for Chrome DevTools
 * @param componentName - Name of the component being tracked
 * @param startTime - Performance timing start (defaults to navigation start)
 */
export const recordComponentRenderTime = (componentName: string, startTime: number = 0) => {
  const renderTime = performance.now();
  const timeElapsed = startTime > 0 ? renderTime - startTime : renderTime;

  const metricName = `component.render.${componentName}`;

  recordDatadogRumTiming(metricName, timeElapsed);

  try {
    performance.mark(metricName);

    performance.measure(`${metricName}-measure`, undefined, metricName);
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      console.warn('User Timing API not available:', error);
    }
  }

  if (process.env.NODE_ENV !== 'production') {
    console.log(`[RUM Metric] ${metricName}: ${timeElapsed.toFixed(2)}ms`);
  }

  return timeElapsed;
};

export const recordCheckoutClickToWidgetRender = async (context: CheckoutClickToWidgetRenderContext): Promise<void> => {
  const { durationMs, eventId, stage } = context;

  const actionContext =
    eventId !== undefined || stage !== undefined
      ? ({
          ...(eventId !== undefined ? { eventId } : {}),
          ...(stage !== undefined ? { stage } : {}),
        } as Record<string, unknown>)
      : undefined;

  if (process.env.NODE_ENV === 'development') {
    console.log('[DD RUM checkout click → widget render]', {
      datadogReady: isDatadogInitialized,
      metricName: CHECKOUT_CLICK_TO_WIDGET_RENDER_METRIC_NAME,
      durationMs,
      ...(actionContext ?? {}),
    });
  }

  await recordDatadogRumTiming(CHECKOUT_CLICK_TO_WIDGET_RENDER_METRIC_NAME, durationMs, actionContext);
};
